import { createAsyncThunk, createSlice, miniSerializeError, SerializedError } from "@reduxjs/toolkit";
import { initialResult } from "src/data/mockData";
import { AnalyzedResult, Result } from "src/data/types";
import { apiRequest } from "src/utils/apiRequest";
import { LOADING_STATE } from "src/constants/constants";
import InspectionService from "src/services/inspectionService";
import { isCannedReason } from "src/components/pages/configurationDrivenInspections/inspectionReview/reasonTags";

type UpdateResultPayload = {
  inspection_id: string;
  question_id: string;
  formData: FormData;
};

export type InspectionResultsState = {
  inspectionResults: Result[];
  inspectionResult: Result;
  currentAnalyzedResult: AnalyzedResult;
  evidenceSample: File | string;

  // Reason entered on a prior resolution, suggested to pre-fill the next one
  suggestedResolutionReason: string | null;
  
  // Loading states
  recordManualResultLoading: LOADING_STATE;
  analyzeEvidenceAsyncLoading: LOADING_STATE;
  updateResultLoading: LOADING_STATE;
  
  // Error states
  recordManualResultError: SerializedError | null;
  analyzeEvidenceAsyncError: SerializedError | null;
  updateResultError: SerializedError | null;
};

const initialState: InspectionResultsState = {
  inspectionResults: [],
  inspectionResult: initialResult,
  currentAnalyzedResult: {
    aiReasoning: "",
    aiResult: "",
    analysisTimestamp: "",
  },
  evidenceSample: "",
  suggestedResolutionReason: null,
  
  // Initialize loading states
  recordManualResultLoading: "idle",
  analyzeEvidenceAsyncLoading: "idle",
  updateResultLoading: "idle",
  
  // Initialize error states
  recordManualResultError: null,
  analyzeEvidenceAsyncError: null,
  updateResultError: null,
};

export const recordManualResult = createAsyncThunk(
  "inspectionResults/recordManualResult",
  async (payload: FormData, { rejectWithValue }) => {
    try {
      const data = await InspectionService.recordManualResult(payload);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to record manual result");
    }
  }
);

export const overrideInspectionResult = createAsyncThunk(
  "inspectionResults/overrideInspectionResult",
  async (updateResultPayload: UpdateResultPayload, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/inspections/${updateResultPayload.inspection_id}/questions/${updateResultPayload.question_id}/result/override`,
        method: "PUT",
        data: updateResultPayload.formData,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to override result");
    }
  }
);

export const fixInspectionResult = createAsyncThunk(
  "inspectionResults/fixInspectionResult",
  async (updateResultPayload: UpdateResultPayload, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/inspections/${updateResultPayload.inspection_id}/questions/${updateResultPayload.question_id}/result/fix`,
        method: "PUT",
        data: updateResultPayload.formData,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fix result");
    }
  }
);

type VerifyCheckpointPayload = {
  inspection_id: string;
  question_id: string;
  verified: boolean;
};

/** Verify / un-verify a checkpoint (question-level). The review page re-fetches to reflect it. */
export const verifyCheckpoint = createAsyncThunk(
  "inspectionResults/verifyCheckpoint",
  async (payload: VerifyCheckpointPayload, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/inspections/${payload.inspection_id}/questions/${payload.question_id}/result/verify`,
        method: "PUT",
        data: { verified: payload.verified },
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to verify checkpoint");
    }
  }
);

export const analyzeEvidenceAsync = createAsyncThunk(
  "inspectionResults/analyzeEvidenceAsync",
  async (analyzePayload: FormData, { rejectWithValue }) => {
    console.log('FormData contents:');
    Array.from(analyzePayload.entries()).forEach(([key, value]) => {
      console.log(key, value);
    });
    try {
      const data = await apiRequest({
        url: `/inspections/analyze-evidence-async`,
        method: "POST",
        data: analyzePayload,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to analyze evidence async");
    }
  }
);

export const analyzeObjectPresenceAsync = createAsyncThunk(
  "inspectionResults/analyzeObjectPresenceAsync",
  async (payload: FormData, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/inspections/analyze-object-presence-async`,
        method: "POST",
        data: payload,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to analyze object presence");
    }
  }
);

/** Inspection Results Slice */
const { reducer, actions } = createSlice({
  name: "inspectionResults",
  initialState,
  reducers: {
    setInspectionResults: (state, { payload }) => {
      state.inspectionResults = payload;
    },
    updateInspectionResults: (state, { payload }: { payload: Result }) => {
      const newResultIndex = state.inspectionResults.findIndex(
        (result) => result.questionId === payload.questionId
      );
      if (newResultIndex !== -1) {
        state.inspectionResults[newResultIndex] = payload;
      } else {
        state.inspectionResults.push(payload);
      }
    },
    setInspectionResult: (state, { payload }) => {
      state.inspectionResult = payload;
    },
    resetCurrentAnalyzedResult: (state) => {
      state.currentAnalyzedResult = {
        aiReasoning: "",
        aiResult: "",
        analysisTimestamp: "",
      };
    },
    setCurrentAnalyzedResult: (state, { payload }) => {
      state.currentAnalyzedResult = payload;
    },
    rememberReason: (state, { payload }: { payload: string }) => {
      const reason = payload.trim();
      if (!reason || isCannedReason(reason)) return;
      state.suggestedResolutionReason = reason;
    },
    resetInspectionResults: (state) => {
      state.inspectionResults = [];
      state.inspectionResult = initialResult;
      state.currentAnalyzedResult = {
        aiReasoning: "",
        aiResult: "",
        analysisTimestamp: "",
      };
      state.evidenceSample = "";
      state.suggestedResolutionReason = null;
      state.recordManualResultLoading = "idle";
      state.analyzeEvidenceAsyncLoading = "idle";
      state.updateResultLoading = "idle";
      state.recordManualResultError = null;
      state.analyzeEvidenceAsyncError = null;
      state.updateResultError = null;
    },
  },
  extraReducers: (builder) => {
    // recordManualResult
    builder.addCase(recordManualResult.pending, (state) => {
      state.recordManualResultLoading = "pending";
      state.recordManualResultError = null;
    });
    builder.addCase(recordManualResult.fulfilled, (state, { payload }) => {
      state.currentAnalyzedResult = payload;
      state.recordManualResultLoading = "fulfilled";
    });
    builder.addCase(recordManualResult.rejected, (state, { error }) => {
      state.recordManualResultLoading = "rejected";
      state.recordManualResultError = miniSerializeError(error);
    });
    
    // overrideInspectionResult (reuses the updateResult loading/error fields)
    builder.addCase(overrideInspectionResult.pending, (state) => {
      state.updateResultLoading = "pending";
      state.updateResultError = null;
    });
    builder.addCase(overrideInspectionResult.fulfilled, (state) => {
      state.updateResultLoading = "fulfilled";
    });
    builder.addCase(overrideInspectionResult.rejected, (state, { payload, error }) => {
      state.updateResultLoading = "rejected";
      // rejectWithValue puts the backend message on `payload` (a string); `error`
      // is only the generic "Rejected".
      state.updateResultError = typeof payload === "string"
        ? { message: payload }
        : miniSerializeError(error);
    });

    // verifyCheckpoint (reuses the updateResult loading/error fields)
    builder.addCase(verifyCheckpoint.pending, (state) => {
      state.updateResultLoading = "pending";
      state.updateResultError = null;
    });
    builder.addCase(verifyCheckpoint.fulfilled, (state) => {
      state.updateResultLoading = "fulfilled";
    });
    builder.addCase(verifyCheckpoint.rejected, (state, { payload, error }) => {
      state.updateResultLoading = "rejected";
      state.updateResultError = typeof payload === "string"
        ? { message: payload }
        : miniSerializeError(error);
    });
    
    // analyzeEvidenceAsync
    builder.addCase(analyzeEvidenceAsync.pending, (state) => {
      state.analyzeEvidenceAsyncLoading = "pending";
      state.analyzeEvidenceAsyncError = null;
    });
    builder.addCase(analyzeEvidenceAsync.fulfilled, (state, { payload }) => {
      // Merge results instead of replacing to prevent race conditions
      // when multiple analysis requests are in flight (API + WebSocket)
      payload.results.forEach((newResult: Result) => {
        const existingIndex = state.inspectionResults.findIndex(
          r => r.questionId === newResult.questionId
        );
        if (existingIndex !== -1) {
          // Update existing result for this question
          state.inspectionResults[existingIndex] = newResult;
        } else {
          // Append new result
          state.inspectionResults.push(newResult);
        }
      });
      state.analyzeEvidenceAsyncLoading = "fulfilled";
    });
    builder.addCase(analyzeEvidenceAsync.rejected, (state, { error }) => {
      state.analyzeEvidenceAsyncLoading = "rejected";
      state.analyzeEvidenceAsyncError = miniSerializeError(error);
    });
  },
});

export const {
  setInspectionResults,
  updateInspectionResults,
  setInspectionResult,
  resetCurrentAnalyzedResult,
  setCurrentAnalyzedResult,
  rememberReason,
  resetInspectionResults,
} = actions;

export default reducer;
